/* ─────────────────────────────────────────────────────────────
   Cartridge Works — v3.js
   The work rail: drag, swipe, arrows, keyboard, and no ends.
   Same trick as the cartridge shelf, bigger shelf.
   ───────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  var rail = document.getElementById("work-rail");
  if (!rail) return;

  var base = Array.prototype.slice.call(rail.children);
  if (!base.length) return;

  var arrows = Array.prototype.slice.call(document.querySelectorAll("[data-work-rail]"));
  var still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var EASE = 0.14;              // how hard the rail pulls toward a target

  var target = null;            // scrollLeft we're gliding to, or null
  var down = false, startX = 0, startScroll = 0, moved = false;

  /* ── copies, so there are no ends ───────────────────────── */
  /* The three real cards stay alone in the markup: crawlers and
     no-JS visitors get three case studies, not nine. The copies
     are made here and hidden from assistive tech — same links,
     same destinations, just more shelf to run on. */

  function all() {
    return Array.prototype.slice.call(rail.querySelectorAll(".work-card"));
  }

  function addSet() {
    base.forEach(function (card) {
      var copy = card.cloneNode(true);
      copy.setAttribute("aria-hidden", "true");
      copy.setAttribute("tabindex", "-1");
      rail.appendChild(copy);
    });
  }

  /* Three laps of cards, with the rail parked in the middle one,
     so there's a full lap of slack whichever way you go. A lap
     also has to be at least as wide as the rail — otherwise
     scrolling one would run past the copies. Three cards clears
     that on every width we support, but measure it rather than
     take it on trust. */

  var reps = 1, lap = 0;

  function advance(n) {
    var list = all();
    return list[n] ? list[n].offsetLeft - list[0].offsetLeft : 0;
  }

  function fill() {
    while (all().length < base.length * 3 * reps) addSet();
    lap = advance(base.length * reps);
    if (lap > 0 && lap < rail.clientWidth && reps < 6) { reps++; fill(); return; }
    drift();
  }

  /* The cartridge straddling each card's image edge floats like the
     ones on the shelf, slower and shorter because it is sitting on
     a photograph. Same trick as the shelf's drift(): the step
     doesn't divide into the lap, so copies never line up. */
  function drift() {
    all().forEach(function (card, i) {
      var badge = card.querySelector(".work-cart-badge");
      if (!badge) return;
      var dur = 4.2 + ((i * 0.61) % 2.1);
      badge.style.setProperty("--fl-dur", dur.toFixed(2) + "s");
      badge.style.setProperty("--fl-delay", "-" + ((i * 1.47) % dur).toFixed(2) + "s");
      badge.style.setProperty("--fl-rise", "-" + (3.5 + ((i * 0.43) % 2.5)).toFixed(1) + "px");
    });
  }

  rail.classList.add("is-looped");   // CSS hands scrollLeft over to us
  fill();
  rail.scrollLeft = lap;

  /* Card widths are in vw, so a resize changes the lap. */
  var settle;
  window.addEventListener("resize", function () {
    clearTimeout(settle);
    settle = setTimeout(function () {
      target = null;
      fill();
      if (rail.scrollLeft < lap || rail.scrollLeft >= lap * 2) rail.scrollLeft = lap;
    }, 150);
  });

  /* ── the wrap ───────────────────────────────────────────── */
  /* Leaving the middle lap puts the rail back where it started,
     one lap along. The cards there are identical, so nothing
     moves on screen. Anything mid-flight comes along. */

  function wrap() {
    if (lap <= 0) return;
    var shift = 0;
    if (rail.scrollLeft >= lap * 2) shift = -lap;
    else if (rail.scrollLeft < lap) shift = lap;
    if (!shift) return;
    rail.scrollLeft += shift;
    if (target !== null) target += shift;
    startScroll += shift;
  }

  /* A wheel or a finger outranks a glide already running, or the
     two pull against each other. */
  rail.addEventListener("wheel", function () { target = null; }, { passive: true });
  rail.addEventListener("touchstart", function () { target = null; }, { passive: true });

  /* ── the loop ───────────────────────────────────────────── */
  /* One owner for scrollLeft, same as the shelf. It checks the wrap
     every frame rather than on the scroll event, because touch and
     trackpad scrolling fire that event on their own schedule and a
     wrap that arrives late is a wrap you can see.

     Nothing moves unless something asked it to: with no target and
     no scrolling this is a comparison and a return. */

  function frame() {
    if (target !== null) {
      var diff = target - rail.scrollLeft;
      if (Math.abs(diff) < 0.5) {
        rail.scrollLeft = target;
        target = null;
      } else {
        rail.scrollLeft += diff * EASE;
      }
    }
    wrap();
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);

  function cardStep() {
    var list = all();
    return list.length > 1 ? list[1].offsetLeft - list[0].offsetLeft : rail.clientWidth;
  }

  /* One card per press, from wherever the last press was heading —
     so holding the arrow down stacks jumps instead of fighting
     the glide already in progress. */
  function jump(dir) {
    target = (target === null ? rail.scrollLeft : target) + dir * cardStep();
    if (still) { rail.scrollLeft = target; target = null; }
  }

  arrows.forEach(function (btn) {
    btn.disabled = false;       // nothing to disable: the rail has no ends
    btn.addEventListener("click", function () {
      jump(parseInt(btn.getAttribute("data-work-rail"), 10));
    });
  });

  rail.addEventListener("keydown", function (e) {
    if (e.key === "ArrowRight") { e.preventDefault(); jump(1); }
    if (e.key === "ArrowLeft")  { e.preventDefault(); jump(-1); }
  });

  /* ── drag ───────────────────────────────────────────────── */
  /* For a mouse. Past 6px it's a drag, under it's a click —
     otherwise you'd never be able to open a case study. */

  rail.addEventListener("pointerdown", function (e) {
    if (e.pointerType === "touch") return;
    down = true; moved = false;
    startX = e.clientX;
    startScroll = rail.scrollLeft;
    target = null;              // the hand beats the glide
  });

  rail.addEventListener("pointermove", function (e) {
    if (!down) return;
    /* The button came up somewhere we never heard about — a release
       outside the rail, or a lost event. Without this the rail keeps
       thinking it's held and drags on the next mouse-over. */
    if (e.buttons === 0) { endDrag(e); return; }
    var dx = e.clientX - startX;
    if (!moved && Math.abs(dx) < 6) return;
    if (!moved) {
      moved = true;
      rail.classList.add("is-dragging");
      rail.setPointerCapture(e.pointerId);
    }
    rail.scrollLeft = startScroll - dx;
    wrap();
  });

  function endDrag(e) {
    if (!down) return;
    down = false;
    if (moved) {
      rail.classList.remove("is-dragging");
      if (e && e.pointerId != null && rail.hasPointerCapture(e.pointerId)) {
        rail.releasePointerCapture(e.pointerId);
      }
    }
  }
  /* on window, not the rail: a press that never passed the 6px drag
     threshold sets no pointer capture, so releasing outside the rail
     fired pointerup somewhere else entirely and `down` stayed true */
  window.addEventListener("pointerup", endDrag);
  window.addEventListener("pointercancel", endDrag);
  window.addEventListener("blur", endDrag);
  rail.addEventListener("dragstart", function (e) { e.preventDefault(); });

  /* A drag that ends on a card would otherwise follow the link.
     Delegated, because the copies arrive after this runs. */
  rail.addEventListener("click", function (e) {
    if (moved && e.target.closest(".work-card")) {
      e.preventDefault();
      moved = false;
    }
  });
})();

/* ─────────────────────────────────────────────────────────────
   The drop. Reaching the valley flashes the words and sends the
   elephant off the bottom of the section. Resets when the
   section leaves view, so it plays again next time rather than
   leaving a hole where an elephant used to be.
   ───────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  var section  = document.querySelector(".ai-stance");
  var valley   = document.getElementById("ai-valley");
  var elephant = document.getElementById("ai-elephant");
  if (!section || !valley || !elephant) return;

  var armed = true;

  new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (en.isIntersecting && armed) {
        armed = false;
        /* Measure before the transform exists, or we'd be reading
           a position the animation already moved. Land the bottom
           of the elephant 47% down the valley — source row ~30,
           where the rock turns solid — so its head stays up in
           the transparent sky band. */
        var e = elephant.getBoundingClientRect();
        var v = valley.getBoundingClientRect();
        section.style.setProperty("--fall", Math.round(v.top + v.height * 0.47 - e.bottom) + "px");
        section.classList.add("is-dropped");
      }
    });
  }, { threshold: 0.9 }).observe(valley);

  /* rearm once the whole section is out of the way */
  new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) {
        section.classList.remove("is-dropped");
        armed = true;
      }
    });
  }, { threshold: 0 }).observe(section);
})();


/* ─────────────────────────────────────────────────────────────
   v2.js tints .svc-stage. With the box gone the colour belongs
   to the whole band, so mirror the attribute onto #ch-1.
   ───────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  var stage = document.getElementById("svc-stage");
  var band  = document.getElementById("ch-1");
  if (!stage || !band) return;

  function mirror() {
    band.setAttribute("data-svc-tint", stage.getAttribute("data-svc-tint") || "brand");
  }
  mirror();
  new MutationObserver(mirror).observe(stage, {
    attributes: true,
    attributeFilter: ["data-svc-tint"]
  });
})();

/* ─────────────────────────────────────────────────────────────
   Mobile nav. The rail is the menu — this just opens it.
   ───────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  var toggle = document.getElementById("nav-toggle");
  var topbar = document.querySelector(".topbar");
  var rail   = document.getElementById("nav-rail");
  if (!toggle || !topbar || !rail) return;

  function setOpen(open) {
    topbar.classList.toggle("nav-open", open);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    toggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
  }

  toggle.addEventListener("click", function (e) {
    e.stopPropagation();
    setOpen(!topbar.classList.contains("nav-open"));
  });

  /* picking a channel closes it */
  rail.addEventListener("click", function (e) {
    if (e.target.closest(".rail-btn")) setOpen(false);
  });

  document.addEventListener("click", function (e) {
    if (!topbar.contains(e.target)) setOpen(false);
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && topbar.classList.contains("nav-open")) {
      setOpen(false);
      toggle.focus();
    }
  });

  /* back to desktop while it's open would leave the class stuck */
  window.matchMedia("(min-width: 881px)").addEventListener("change", function (m) {
    if (m.matches) setOpen(false);
  });
})();

/* ─────────────────────────────────────────────────────────────
   The shelf is bled out of its column to the full width of the
   window. CSS does the bleeding and the fade; it just needs to
   know where the column's edges are, in viewport pixels.

   A ResizeObserver rather than a resize listener, because the
   column also moves when the fonts land and when the detail
   panel changes size — neither of which fires a window resize.
   ───────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  var wrap  = document.querySelector(".svc-split .cart-rail-wrap");
  var frame = document.querySelector(".svc-left");
  if (!wrap || !frame) return;

  var was = "";

  function measure() {
    var f = frame.getBoundingClientRect();
    var vw = document.documentElement.clientWidth;
    var now = Math.round(f.left) + "/" + Math.round(f.right) + "/" + vw;
    if (now === was) return;
    was = now;
    wrap.style.setProperty("--frame-l", Math.round(f.left) + "px");
    wrap.style.setProperty("--frame-r", Math.round(f.right) + "px");
    wrap.style.setProperty("--bleed-w", vw + "px");
    /* The shelf sizes itself against the rail's width and places
       its shells against these edges, so it has to hear about a
       change. Guarded on the values above, so this can't chase
       its own tail. */
    window.dispatchEvent(new Event("cw:frame"));
  }

  measure();
  window.addEventListener("resize", measure);
  if (window.ResizeObserver) new ResizeObserver(measure).observe(frame);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
})();

/* ─────────────────────────────────────────────────────────────
   The shelf: one loop owns scrollLeft.

   The rail is bled out to the full width of the window, so it
   has to be filled right across it — a lap of shells has to be
   at least as wide as the window or wrapping would show daylight
   at one end. Copies are added here until that holds, three laps
   of them, and the rail parks in the middle lap so there is a
   full lap of shells waiting on both sides.

   Only the opaque column counts as the frame. A shell drifting
   outside it is scenery: it can't be hovered, clicked or tinted,
   and anything you do select is brought back to the middle of
   the frame rather than the middle of the window.
   ───────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  var rail  = document.getElementById("cart-rail");
  var stage = document.getElementById("svc-stage");
  if (!rail || !stage) return;

  var ORDER = ["brand", "social", "web", "decks", "email", "marketing"];
  var SPEED = 0.35;      // px per frame while drifting
  var EASE  = 0.12;      // how hard it pulls toward a target
  var still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var paused = false;
  var target = null;     // scrollLeft we're gliding to, or null

  /* the six real shells; everything past them is a copy */
  var base = Array.prototype.slice.call(rail.querySelectorAll(".cart")).slice(0, ORDER.length);
  if (!base.length) return;

  function all() { return Array.prototype.slice.call(rail.querySelectorAll(".cart")); }

  function addSet() {
    base.forEach(function (c) {
      var copy = c.cloneNode(true);
      copy.classList.remove("active");
      copy.setAttribute("aria-hidden", "true");
      copy.setAttribute("tabindex", "-1");
      copy.setAttribute("aria-pressed", "false");
      rail.appendChild(copy);
    });
  }

  /* ── the lap ────────────────────────────────────────────── */

  var reps = 1, lap = 0, slots = [], railX = 0;

  function advance(n) {
    var l = all();
    return l[n] ? l[n].offsetLeft - l[0].offsetLeft : 0;
  }

  function fill() {
    while (all().length < base.length * 3 * reps) addSet();
    lap = advance(base.length * reps);
    if (lap > 0 && lap < rail.clientWidth && reps < 8) { reps++; return fill(); }
    cache();
  }

  /* Positions read once and kept. The loop checks every shell
     against the frame on every frame; doing that with
     getBoundingClientRect would mean forty layout reads a frame
     for a number that only changes on resize. */
  function cache() {
    railX = rail.getBoundingClientRect().left;
    slots = all().map(function (c, i) {
      drift(c, i);
      return { el: c, l: c.offsetLeft, w: c.offsetWidth, svc: c.getAttribute("data-svc"), in: null };
    });
  }

  /* index-v2.css times the first six shells by hand, which was the
     whole shelf before it looped. There are thirty-six now, and the
     rest were all inheriting one duration and a zero delay, so most
     of the rail bobbed in step. Timing comes from the shell's place
     in the line instead: the steps don't divide into six, so no two
     neighbours share a beat and a lap never repeats the one before. */
  function drift(el, i) {
    var dur = 3.1 + ((i * 0.53) % 1.7);
    el.style.setProperty("--fl-dur", dur.toFixed(2) + "s");
    el.style.setProperty("--fl-delay", "-" + ((i * 1.31) % dur).toFixed(2) + "s");
    el.style.setProperty("--fl-rise", "-" + (5.5 + ((i * 0.37) % 3.5)).toFixed(1) + "px");
  }

  function frame() {
    var cs = getComputedStyle(rail);
    var l = parseFloat(cs.getPropertyValue("--frame-l"));
    var r = parseFloat(cs.getPropertyValue("--frame-r"));
    if (isNaN(l) || isNaN(r)) { l = railX; r = railX + rail.clientWidth; }
    return { l: l, r: r, mid: (l + r) / 2 };
  }

  fill();
  rail.scrollLeft = lap;

  function relayout() {
    target = null;
    fill();
    if (rail.scrollLeft < lap || rail.scrollLeft >= lap * 2) rail.scrollLeft = lap;
    gate();
  }
  window.addEventListener("resize", relayout);
  window.addEventListener("cw:frame", relayout);

  /* ── in frame, or scenery ───────────────────────────────── */

  function gate() {
    var f = frame(), sl = rail.scrollLeft;
    for (var i = 0; i < slots.length; i++) {
      var s = slots[i];
      var mid = railX + s.l - sl + s.w / 2;
      var inside = mid > f.l && mid < f.r;
      if (s.in !== inside) {
        s.in = inside;
        s.el.classList.toggle("is-out", !inside);
      }
    }
  }

  /* ── selecting ──────────────────────────────────────────── */
  /* app.js wired the six shells it found at load; the copies
     arrived afterwards. So selection is handled here, delegated,
     and it covers every copy at once — whichever one of them is
     in the frame is the one lit up. */

  function select(svc) {
    slots.forEach(function (s) {
      var on = s.svc === svc;
      s.el.classList.toggle("active", on);
      s.el.setAttribute("aria-pressed", on ? "true" : "false");
    });
    Array.prototype.slice.call(document.querySelectorAll(".svc-panel")).forEach(function (p) {
      p.hidden = p.getAttribute("data-panel") !== svc;
    });
    stage.setAttribute("data-svc-tint", svc);
  }

  function current() {
    var on = rail.querySelector(".cart.active");
    var i = ORDER.indexOf(on ? on.getAttribute("data-svc") : "brand");
    return i < 0 ? 0 : i;
  }

  /* ── centring ───────────────────────────────────────────── */

  function centreOf(el) {
    var r = el.getBoundingClientRect();
    return r.left + r.width / 2;
  }

  function centre(el) {
    target = rail.scrollLeft + (centreOf(el) - frame().mid);
    if (still) { rail.scrollLeft = target; target = null; }
  }

  /* every copy is as valid as the original; take the one already
     nearest the frame so the shelf goes the short way round */
  function nearestCopy(svc) {
    var mid = frame().mid, best = null, bestDist = Infinity;
    slots.forEach(function (s) {
      if (s.svc !== svc) return;
      var d = Math.abs((railX + s.l - rail.scrollLeft + s.w / 2) - mid);
      if (d < bestDist) { bestDist = d; best = s.el; }
    });
    return best;
  }

  function jump(dir) {
    var svc = ORDER[(current() + dir + ORDER.length) % ORDER.length];
    var el = nearestCopy(svc);
    if (!el) return;
    select(svc);
    centre(el);
  }

  /* v2.js wired these to scrollBy, which this loop overwrites on the
     next frame — so they did nothing. Replacing the nodes drops those
     listeners. */
  Array.prototype.slice.call(document.querySelectorAll("[data-rail]")).forEach(function (old) {
    var btn = old.cloneNode(true);
    btn.disabled = false;      // nothing to disable: the shelf has no ends
    old.parentNode.replaceChild(btn, old);
    btn.addEventListener("click", function () {
      jump(parseInt(btn.getAttribute("data-rail"), 10));
    });
  });

  /* ── pointer ────────────────────────────────────────────── */

  rail.addEventListener("click", function (e) {
    var cart = e.target.closest(".cart");
    if (!cart || cart.classList.contains("is-out")) return;
    select(cart.getAttribute("data-svc"));
    centre(cart);
  });

  /* hover paints the band — delegated, so the copies do it too */
  rail.addEventListener("pointerover", function (e) {
    var cart = e.target.closest(".cart");
    if (cart && !cart.classList.contains("is-out")) {
      stage.setAttribute("data-svc-tint", cart.getAttribute("data-svc"));
    }
  });
  rail.addEventListener("mouseleave", function () {
    stage.setAttribute("data-svc-tint", ORDER[current()]);
  });

  /* a keyboard user can reach a shell that has drifted out of the
     frame; bring it back in rather than leaving focus in the fog */
  rail.addEventListener("focusin", function (e) {
    var cart = e.target.closest(".cart");
    if (cart) centre(cart);
  });

  function hold() { paused = true; }
  function go()   { paused = false; }

  rail.addEventListener("pointerenter", hold);
  rail.addEventListener("pointerleave", go);
  rail.addEventListener("pointerdown", function () { hold(); target = null; });
  window.addEventListener("pointerup", go);
  rail.addEventListener("focusin", hold);
  rail.addEventListener("focusout", function (e) {
    if (!rail.contains(e.relatedTarget)) go();
  });
  document.addEventListener("visibilitychange", function () { paused = document.hidden; });

  /* ── the loop ───────────────────────────────────────────── */

  function step() {
    if (lap > 0) {
      if (target !== null) {
        var diff = target - rail.scrollLeft;
        if (Math.abs(diff) < 0.5) {
          rail.scrollLeft = target;
          target = null;
        } else {
          rail.scrollLeft += diff * EASE;
        }
      } else if (!paused) {
        rail.scrollLeft += SPEED;
      }

      /* back to the middle lap, carrying any in-flight target */
      if (rail.scrollLeft >= lap * 2) {
        rail.scrollLeft -= lap;
        if (target !== null) target -= lap;
      } else if (rail.scrollLeft < lap) {
        rail.scrollLeft += lap;
        if (target !== null) target += lap;
      }

      gate();
    }
    requestAnimationFrame(step);
  }

  gate();
  requestAnimationFrame(step);
})();
